/**
 * Choose the IMAM person a Classroom row is (ADR 0037, decision 4). The
 * registrar types the search and picks a register row; nothing is prefilled
 * or suggested from the Classroom name or email. A student row may link a
 * person who holds no student record yet; the apply issues one on that
 * person, so the review then asks for its cohort or admitted year.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { RELATIONSHIP_LABEL } from "@/components/people/person-record";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { PEOPLE_QUERY_KEY, peopleApi, type PeopleRegisterRow } from "@/lib/api/people";
import { personChoice } from "@/lib/person-name";

import type { ChosenPerson } from "./intake-plan";

const RESULT_LIMIT = 8;

function personMeta(person: PeopleRegisterRow): string {
  return [
    person.email,
    person.sid ? `SID ${person.sid}` : null,
    person.relationships.map((relationship) => RELATIONSHIP_LABEL[relationship]).join(" + ") || null,
  ].filter(Boolean).join(" · ");
}

export function IntakePersonSearch({
  rowLabel,
  register,
  person,
  onChange,
}: {
  /** The Classroom name of the row, naming the search for assistive technology. */
  rowLabel: string;
  register: "teacher" | "student";
  person: ChosenPerson | null;
  onChange: (person: ChosenPerson | null) => void;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const settled = useDebouncedValue(search.trim());
  const searchInput = useRef<HTMLInputElement | null>(null);
  const changeButton = useRef<HTMLButtonElement | null>(null);
  const focusNext = useRef<"change" | "search" | null>(null);

  useEffect(() => {
    if (focusNext.current === "change") changeButton.current?.focus();
    if (focusNext.current === "search") searchInput.current?.focus();
    focusNext.current = null;
  }, [person]);

  const results = useQuery({
    queryKey: [...PEOPLE_QUERY_KEY, "classroom-intake-person", settled],
    queryFn: () => peopleApi.list({ view: "all", q: settled, page_size: RESULT_LIMIT }),
    enabled: !person && settled.length > 0,
  });
  const rows = results.data?.rows ?? [];

  if (person) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-control bg-well p-3">
        <span className="min-w-0 text-ui font-medium text-foreground [overflow-wrap:anywhere]">{person.name}</span>
        <Button
          ref={changeButton}
          type="button"
          size="sm"
          variant="outline"
          aria-label={`Change the person for ${rowLabel}`}
          onClick={() => {
            focusNext.current = "search";
            onChange(null);
          }}
        >
          Change person
        </Button>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <Label htmlFor={`${id}-search`}>Search People for {rowLabel}</Label>
      <Input
        ref={searchInput}
        id={`${id}-search`}
        type="search"
        autoComplete="off"
        placeholder="Name, email, or student ID"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.preventDefault();
        }}
      />
      <div aria-live="polite" className="grid gap-2 empty:hidden">
        {settled.length > 0 && <>
          {results.isLoading && <p className="text-meta text-muted-foreground">Searching People</p>}
          {results.isError && (
            <div className="flex flex-wrap items-center gap-3 text-meta">
              <p className="text-destructive">People could not be searched.</p>
              <Button type="button" size="sm" variant="outline" onClick={() => void results.refetch()}>Try again</Button>
            </div>
          )}
          {results.isSuccess && rows.length === 0 && (
            <p className="text-meta text-muted-foreground">No one in People matches. Search again, or create a new person.</p>
          )}
          {rows.length > 0 && (
            <ul aria-label={`People matching the search for ${rowLabel}`} className="grid">
              {rows.map((row) => {
                const name = personChoice(row.display_name, row.email);
                const meta = [personMeta(row), register === "student" && !row.student_id ? "No student record yet" : null]
                  .filter(Boolean).join(" · ");
                return (
                  <li key={row.person_id} className="border-t border-rule first:border-t-0">
                    <button
                      type="button"
                      className="flex min-h-11 w-full min-w-0 items-start justify-between gap-3 px-2 py-2.5 text-left outline-none transition-colors duration-[var(--motion-fast)] hover:bg-well focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      onClick={() => {
                        focusNext.current = "change";
                        onChange({ person_id: row.person_id, name, student_id: row.student_id, sid: row.sid, student_status: row.student_status });
                      }}
                    >
                      <span className="grid min-w-0 gap-0.5 [overflow-wrap:anywhere]">
                        <span className="text-ui font-medium text-foreground">{name}</span>
                        {meta && <span className="text-meta text-muted-foreground">{meta}</span>}
                      </span>
                      <span className="shrink-0 text-meta font-semibold text-primary">Choose</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {results.data && results.data.total > rows.length && (
            <p className="text-meta text-muted-foreground">
              Showing {rows.length} of {results.data.total}. Search more precisely to find someone else.
            </p>
          )}
        </>}
      </div>
    </div>
  );
}
